import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  Score
} from '@mui/icons-material';
import { baseUrl, config, requestID } from '../common/utils';
import axios from 'axios';
import { IonContent, IonPage } from '@ionic/react';

const SurveyReport = ({ token }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState({ table: true });
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const configWithAuth = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    }
  };

  const fetchReport = async () => {
    try {
      setLoading((prev) => ({ ...prev, table: true }));
      const payload = { requestID: requestID() };
      
      const res = await axios.post(
        `${baseUrl()}survey/report`,
        payload,
        configWithAuth
      );

      if (res.data.success) {
        setReportData(res.data);
      }
    } catch (error) {
      setError('Failed to fetch survey report');
      console.error('Error:', error);
    } finally {
      setLoading((prev) => ({ ...prev, table: false }));
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  if (loading.table) {
    return (
      <IonPage>
        <IonContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonContent>
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3
          }}>
            <Analytics /> Survey Report
          </Typography>

          {/* Average Score Card */}
          <Card sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            borderRadius: 2, 
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Overall Average Score
              </Typography>
              <Typography variant="h1" sx={{ fontWeight: 700, mb: 2 }}>
                {reportData.averageSurvey}/10
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={reportData.averageSurvey * 10} 
                sx={{ 
                  height: 12, 
                  borderRadius: 6,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white'
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Individual Scores */}
          <Typography variant="h5" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Score /> Detailed Scores
          </Typography>
          
          <Grid container spacing={3}>
            {reportData.surveyScores.map((score, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {score.category}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    {score.score}/10
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={score.score * 10} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Rated on: {new Date(score.created_at).toLocaleDateString()}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Performance Summary */}
          {reportData.averageSurvey && (
            <Paper sx={{ p: 3, borderRadius: 2, mt: 4, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp /> Performance Summary
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your overall performance score of <strong>{reportData.averageSurvey}/10</strong> indicates{' '}
                {reportData.averageSurvey >= 8 ? 'exceptional performance' :
                 reportData.averageSurvey >= 6 ? 'strong performance' :
                 reportData.averageScore >= 4 ? 'satisfactory performance' :
                 'areas needing improvement'}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Continue focusing on your strengths while working on areas that need development.
              </Typography>
            </Paper>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SurveyReport;